import { useCallback, useEffect, useState } from 'react';
import type { Patient } from '@/types/patients';
import { patientRowDomId } from './PatientList';

/**
 * Drives ↑/↓/Enter navigation over a `PatientList`'s results from a keydown
 * handler placed on the wrapper that ALSO contains the search input — so
 * keystrokes typed in the focused input (which bubble to the wrapper) move the
 * highlight and Enter selects the highlighted patient.
 *
 * `PatientList` is controlled: feed it `highlightedId` and wire `setPatients`
 * to its `onResultsChange`. The hook clamps the index, resets to the top
 * whenever the result set changes, and exposes `activeDescendantId` for
 * `aria-activedescendant` on the input.
 */
export function useResultNavigation(onSelect: (p: Patient) => void) {
  const [patients, setPatientsState] = useState<Patient[]>([]);
  const [index, setIndex] = useState(0);

  // Reset the highlight to the first row whenever the visible results change.
  // We compare by id-list identity so an unchanged list (same refs) is a no-op.
  const setPatients = useCallback((next: Patient[]) => {
    setPatientsState((prev) => {
      if (sameIds(prev, next)) return prev;
      setIndex(0);
      return next;
    });
  }, []);

  // Keep the index in range if the list shrinks out from under us.
  useEffect(() => {
    setIndex((i) => (patients.length === 0 ? 0 : Math.min(i, patients.length - 1)));
  }, [patients.length]);

  const highlighted = patients[Math.min(index, Math.max(0, patients.length - 1))];
  const highlightedId = highlighted?.id ?? null;
  const activeDescendantId = highlightedId ? patientRowDomId(highlightedId) : undefined;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (patients.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIndex((i) => Math.min(patients.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const p = patients[Math.min(index, patients.length - 1)];
        if (p) onSelect(p);
      }
      // Any other key (typing, etc.) falls through untouched.
    },
    [patients, index, onSelect],
  );

  return { highlightedId, activeDescendantId, onKeyDown, setPatients };
}

function sameIds(a: Patient[], b: Patient[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.id !== b[i]!.id) return false;
  }
  return true;
}
